import React from 'react';

export interface InputProps {
  label: string;
  type?: 'text' | 'email' | 'password' | 'tel';
  id?: string;
  name?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  maxLength?: number;
  className?: string;
  icon?: string;
  rightIcon?: string;
  onRightIconClick?: () => void;
  minLength?: number;
  error?: string;
  isValid?: boolean;
  // react-hook-form 지원
  register?: {
    name: string;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
    ref: React.Ref<HTMLInputElement>;
  };
}

export function Input({
  label,
  type = 'text',
  id,
  name,
  value = '',
  onChange,
  placeholder,
  required = false,
  disabled = false,
  readOnly = false,
  maxLength,
  className = '',
  icon,
  rightIcon,
  onRightIconClick,
  minLength,
  error,
  isValid,
  register,
}: Readonly<InputProps>) {
  const inputId = id || name || label.toLowerCase().replace(/\s+/g, '-');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e);
  };

  return (
    <div className={className}>
      <label
        htmlFor={inputId}
        className="block text-sm font-medium text-gray-700 mb-2"
      >
        {label} {required && <span className="text-red-500">(필수)</span>}
      </label>
      <div className="relative">
        {icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <i className={`${icon} text-gray-400 text-sm`}></i>
          </div>
        )}
        {register ? (
          <input
            id={inputId}
            className={`w-full ${icon ? 'pl-10' : 'pl-4'} ${
              rightIcon ? 'pr-10' : 'pr-4'
            } py-3 border ${
              error
                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                : isValid === true
                  ? 'border-green-300 focus:ring-green-500 focus:border-green-500'
                  : 'border-gray-300 focus:ring-blue-500 focus:border-transparent'
            } rounded-xl focus:ring-2 transition-all text-sm`}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            readOnly={readOnly}
            maxLength={maxLength}
            minLength={minLength}
            type={type}
            {...register}
          />
        ) : (
          <input
            id={inputId}
            name={name}
            value={value}
            onChange={handleChange}
            className={`w-full ${icon ? 'pl-10' : 'pl-4'} ${
              rightIcon ? 'pr-10' : 'pr-4'
            } py-3 border ${
              error
                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                : isValid === true
                  ? 'border-green-300 focus:ring-green-500 focus:border-green-500'
                  : 'border-gray-300 focus:ring-blue-500 focus:border-transparent'
            } rounded-xl focus:ring-2 transition-all text-sm`}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            readOnly={readOnly}
            maxLength={maxLength}
            minLength={minLength}
            type={type}
          />
        )}
        {rightIcon && (
          <button
            type="button"
            className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
            onClick={onRightIconClick}
            disabled={disabled || readOnly}
          >
            <i
              className={`${rightIcon} text-gray-400 hover:text-gray-600 text-sm`}
            />
          </button>
        )}
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      {isValid === true && !error && (
        <p className="mt-1 text-sm text-green-600">유효한 형식입니다</p>
      )}
    </div>
  );
}
